import { getStoredValue, setStoredValue } from 'common';
import React from 'react';

/**
 * Creates a deep or shallow clone of an object.
 * 
 * @template Type
 * @param {Type} src object to clone
 * @param {boolean} [deep=false] 
 * @returns {Type} clone of src
 */
export function clone(src, deep = false) {
  const wm = (deep && new WeakMap());
  return (function _clone(src) {
    if (typeof src !== "object") {
      return src;
    } else switch (Object.prototype.toString.call(src)) {
    case "[object Object]":
    case "[object Array]":
      if (wm && wm.has(src)) return wm.get(src);
      const isarray = Array.isArray(src);
      const dst = (isarray ? [] : Object.create(Object.getPrototypeOf(src)));
      const properties = Object.getOwnPropertyDescriptors(src);
      if (deep) {
        for (const [name, desc] of Object.entries(properties)) {
          if (desc.hasOwnProperty("value")) {
            properties[name] = {...desc, value: _clone(desc.value)};
          }
        }
      }
      Object.defineProperties(dst, properties);
      if (wm) wm.set(src, dst);
      return dst;
    case "[object WeakMap]":
      // weak maps can't be cloned
      return src;
    case "[object Map]":
      if (deep) {
        if (wm.has(src)) return wm.get(src);
        const dst = new Map(_clone(src.entries()));
        wm.set(dst);
        return dst;
      }
      // otherwise fall through
    default:
      return src && new src.constructor(src.valueOf());
    }
  })(src);
}

/**
 * Deep copy version of Object.assign
 * 
 * @template Type
 * @param {Type} dst object to extend
 * @param {Type[]} args object(s) to copy into dst
 * @returns {Type} first argument
 */
export function deepCopy(dst, ...args) {
  args.forEach(src => {
    (function _copy(dst, src) {
      for (let key in src) {
        let to = dst[key], from = src[key];
        if (to === from || from === undefined) continue;
        if (Array.isArray(src) && from == null) continue; // special case for array with holes
        if (typeof from !== "object") {
          dst[key] = from;
        } else switch (Object.prototype.toString(from)) {
        case "[object Object]":
        case "[object Array]":
          if (!to || (Object.getPrototypeOf(to) !== Object.getPrototypeOf(from) && !Array.isArray(to))) {
            to = dst[key] = (Array.isArray(from) ? [] : Object.create(Object.getPrototypeOf(from)));
          }
          _copy(to, from);
          break;
        case "[object WeakMap]":
          break;
        default:
          dst[key] = new from.constructor(from.valueOf());
        }
      }
    })(dst, src);
  });
  return dst;
}

export function keyedList(list) {
  return list.map((value, key) => {
    if (React.isValidElement(value)) {
      return React.cloneElement(value, {key});
    } else {
      return value;
    }
  });
}

export function chainedRef(...refs) {
  return value => {
    for (const ref of refs) {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref) {
        ref.current = value;
      }
    }
  };
}

export function usePersistentState(key, initialValue) {
  const [value, setValue] = React.useState(() => getStoredValue(key, initialValue));
  React.useEffect(() => setStoredValue(key, value), [key, value]);
  return [value, setValue];
}

export const searchRegex = (str, middle) => {
  const prefix = (middle && !str.match(/^\s/) ? "" : "\\b") + "(";
  const suffix = ")" + (str.match(/\s$/) ? "\\b" : "");
  str = str.trim().replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
  if (!str) return;
  return new RegExp(prefix + str + suffix, "i");
};
